import { useState } from 'react';
import { AlertTriangle, Info, ChevronDown, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/cn';
import type { EPCR } from '@/types';
import { activeSets } from '@/lib/conditional-cascade';
import { RuleTag } from '@/components/RuleTag';

/**
 * ConditionalSetBanner — EMSPCR Foundation v1.0 §3.3.
 *
 * Sits at the top of the ePCR workspace and shows every conditional
 * set currently active on this run (Trauma, Cardiac Arrest, Stroke,
 * STEMI, Behavioral, Pediatric, OB, Refusal, Overdose, etc.).
 *
 * Tapping the row expands a plain-language "why is this here?" card
 * listing each standard that mandates the set, so the crew never
 * has to wonder why a section appeared. The base set is always
 * active but dropped from the banner (it's the implicit baseline).
 *
 * Collapsed by default when no non-base sets are active.
 */

interface Props {
  epcr: EPCR;
  className?: string;
}

export function ConditionalSetBanner({ epcr, className }: Props) {
  const sets = activeSets(epcr).filter((s) => s.id !== 'base');
  const [expanded, setExpanded] = useState(false);

  if (sets.length === 0) {
    return null;
  }

  const mostCritical = sets[0]; // activeSets returns parents-first already
  const urgencyTone =
    sets.some((s) => s.id === 'cardiac_arrest' || s.id === 'stemi' || s.id.startsWith('trauma'))
      ? 'amber'
      : 'slate';

  const toneClass = urgencyTone === 'amber'
    ? 'border-amber-700 bg-amber-950/30 text-amber-200'
    : 'border-slate-700 bg-slate-900/70 text-slate-300';

  return (
    <section
      className={cn('border px-3 py-1.5', toneClass, className)}
      aria-label="Active conditional documentation sets"
    >
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="flex w-full items-center gap-2 text-left"
      >
        {urgencyTone === 'amber' ? (
          <AlertTriangle size={14} className="shrink-0" />
        ) : (
          <Info size={14} className="shrink-0" />
        )}
        <span className="text-[10px] font-bold uppercase tracking-widest shrink-0">
          Required sets active
        </span>
        <span className="flex flex-wrap gap-1 flex-1 min-w-0">
          {sets.slice(0, 6).map((s) => (
            <span
              key={s.id}
              className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-widest border border-current/40 bg-black/20"
              title={s.name}
            >
              {s.name}
            </span>
          ))}
          {sets.length > 6 && (
            <span className="text-[10px] opacity-70">+{sets.length - 6}</span>
          )}
        </span>
        {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
      </button>

      {expanded && (
        <div className="mt-2 space-y-2 border-t border-current/30 pt-2">
          <p className="text-[11px] leading-snug opacity-90">
            These sets expand the NEMSIS documentation requirements for this run.
            Fields gated by each set become <strong>Required at Post</strong>.
            Tap a rule pill to read which standard mandates the set.
          </p>
          <ul className="space-y-1.5">
            {sets.map((s) => (
              <li
                key={s.id}
                className="flex items-start gap-2 border border-current/20 bg-black/20 px-2 py-1.5"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs font-bold">{s.name}</span>
                    {s.parent && (
                      <span className="text-[9px] font-mono uppercase tracking-widest opacity-70">
                        sub-set
                      </span>
                    )}
                  </div>
                  <div className="text-[11px] leading-snug mt-0.5 opacity-90">
                    {s.rationale}
                  </div>
                  {s.elements.length > 0 && (
                    <div className="text-[10px] font-mono mt-1 opacity-70">
                      Gates: {s.elements.join(' · ')}
                    </div>
                  )}
                </div>
                <div className="flex flex-wrap gap-1 shrink-0">
                  {s.ruleSources.map((std) => (
                    <RuleTag
                      key={std}
                      compact
                      sources={[{ standard: std, rationale: `Cited by ${s.name}: ${s.rationale}` }]}
                    />
                  ))}
                </div>
              </li>
            ))}
          </ul>
          {mostCritical && (
            <div className="text-[10px] opacity-70 font-mono uppercase tracking-widest">
              Top priority: {mostCritical.name}
            </div>
          )}
        </div>
      )}
    </section>
  );
}
